// Room Value Transformation Utilities
// Handles display-time transformation of room types, themes, and dimensions from API values to display names

import { ROOM_TYPE_MAPPINGS, ROOM_THEME_MAPPINGS } from "./api-value-mappings"

/**
 * Transforms a raw room type value to its display name
 * Applies fallback rules for unmapped values
 */
export function transformRoomTypeValue(rawValue: string): string {
  if (!rawValue || typeof rawValue !== "string") {
    return rawValue || ""
  }

  // 1. Direct mapping lookup first
  const directMapping = ROOM_TYPE_MAPPINGS.find((mapping) => mapping.value === rawValue)
  if (directMapping) {
    return directMapping.name
  }

  // 2. Apply fallback transformation rules
  const transformedValue = applyFallbackRules(rawValue)
  logUnmappedValue("roomType", rawValue, transformedValue)
  return transformedValue
}

/**
 * Transforms a raw room theme value to its display name
 * Applies fallback rules for unmapped values
 */
export function transformRoomThemeValue(rawValue: string): string {
  if (!rawValue || typeof rawValue !== "string") {
    return rawValue || ""
  }

  // 1. Direct mapping lookup first
  const directMapping = ROOM_THEME_MAPPINGS.find((mapping) => mapping.value === rawValue)
  if (directMapping) {
    return directMapping.name
  }

  // 2. Apply fallback transformation rules
  const transformedValue = applyFallbackRules(rawValue)
  logUnmappedValue("roomTheme", rawValue, transformedValue)
  return transformedValue
}

/**
 * Transforms a raw room dimension value to its display name
 * Applies specific rules for dimension values
 * Defaults to "Medium" when value is empty or null
 */
export function transformRoomDimensionValue(rawValue: string): string {
  if (!rawValue || typeof rawValue !== "string" || rawValue.trim() === "") {
    return "Medium"
  }

  let transformed = rawValue.trim()

  // Rule 1: Handle size abbreviations
  if (transformed.toLowerCase() === "xl" || transformed.toLowerCase() === "x-large") {
    return "Extra Large"
  }
  if (transformed.toLowerCase() === "xs" || transformed.toLowerCase() === "x-small") {
    return "Extra Small"
  }

  // Rule 2: Truncate long values - extract just the size word before parentheses
  if (transformed.includes("(")) {
    transformed = transformed.split("(")[0].trim()
  }

  // Rule 3: Remove hyphens and capitalize each word
  if (transformed.includes("-")) {
    transformed = transformed
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  } else {
    // Rule 4: Capitalize first letter of each word
    transformed = transformed
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  }

  return transformed
}

/**
 * Applies fallback transformation rules for unmapped values
 * Rules:
 * 1. Replace "master" with "primary"
 * 2. Handle hyphens: "kids-bedroom" → "Kids Bedroom"
 * 3. Capitalize first letter: "garage" → "Garage"
 * 4. Remove "-theme" suffix if present
 */
function applyFallbackRules(value: string): string {
  let transformed = value

  // Rule 1: Replace "master" with "primary"
  transformed = transformed.replace(/master/gi, "primary")

  // Rule 4: Remove "-theme" suffix if present
  transformed = transformed.replace(/-theme$/i, "")

  // Rule 2: Handle hyphens - split on hyphens and capitalize each word
  if (transformed.includes("-")) {
    transformed = transformed
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  } else {
    // Rule 3: Capitalize first letter if no hyphens
    transformed = transformed.charAt(0).toUpperCase() + transformed.slice(1).toLowerCase()
  }

  return transformed
}

/**
 * Development helper to log unmapped values
 */
export function logUnmappedValue(
  type: "roomType" | "roomTheme" | "roomDimension",
  rawValue: string,
  transformedValue: string,
): void {
  if (process.env.NODE_ENV === "development") {
    console.warn(`[Room Mapping] Unmapped ${type}: "${rawValue}" → "${transformedValue}"`)
  }
}

/**
 * Removes date suffixes from project names and transforms embedded room values
 * Transforms "mid-century-theme Calvins Room Design - Saved on 9/22/2025 5:11:49 PM" → "Mid Century Calvins Room Design"
 */
export function transformProjectName(projectName: string): string {
  if (!projectName || typeof projectName !== "string") {
    return projectName || ""
  }

  // Step 1: Remove " - Saved on [date/time]" pattern
  let cleaned = projectName.replace(/ - Saved on .+$/i, "").trim()

  // Step 2: Transform embedded room theme/type values
  cleaned = transformEmbeddedRoomValues(cleaned)

  return cleaned
}

/**
 * Transforms embedded room theme/type values within project names
 * Handles cases like "mid-century-theme Calvins Room" → "Mid Century Calvins Room"
 */
function transformEmbeddedRoomValues(text: string): string {
  let transformed = text

  // Transform known room theme patterns
  ROOM_THEME_MAPPINGS.forEach((mapping) => {
    const regex = new RegExp(`\\b${mapping.value}\\b`, "gi")
    transformed = transformed.replace(regex, mapping.name)
  })

  // Transform known room type patterns
  ROOM_TYPE_MAPPINGS.forEach((mapping) => {
    const regex = new RegExp(`\\b${mapping.value}\\b`, "gi")
    transformed = transformed.replace(regex, mapping.name)
  })

  // Apply fallback rules to any remaining hyphenated values that look like room values
  transformed = transformed.replace(/\b([a-z]+-[a-z-]+(?:-theme)?)\b/gi, (match) => {
    return applyFallbackRules(match)
  })

  return transformed
}
